import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AppShell } from '@/components/px/AppShell'
import { EmptyPanel } from '@/components/px/EmptyPanel'
import { ErrorPanel } from '@/components/px/ErrorPanel'
import { ForbiddenPanel } from '@/components/px/ForbiddenPanel'
import { LoadingPanel } from '@/components/px/LoadingPanel'
import { SectionLabel } from '@/components/px/SectionLabel'
import { StatusPill, type StatusTone } from '@/components/px/StatusPill'
import { adminNav } from '@/components/px/nav'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import {
  automationRetryClicked,
  fetchAutomationProjects,
  fetchContextIndexStatus,
  fetchSchedulerJobs,
  jobResultDismissed,
  selectAutomationMutationError,
  selectAutomationProjects,
  selectContextIndexCache,
  selectContextIndexError,
  selectContextIndexRun,
  selectJobResult,
  selectRunningIndex,
  selectRunningJobKey,
  selectSchedulerJobs,
  selectSchedulerJobsCache,
  selectSchedulerJobsError,
  triggerContextIndexRun,
  triggerJob
} from '@/features/automation/automationSlice'
import type { ContextIndexRunView, JobRunResult, SchedulerJobView } from '@/features/automation/types'
import type { LlmKeyForm } from '@/features/llmconfig/llmKeyPolicy'
import {
  fetchLlmConfigs,
  llmErrorDismissed,
  llmRetryClicked,
  saveLlmConfig,
  selectLlmCache,
  selectLlmConfigs,
  selectLlmError,
  selectLlmRefusal,
  selectLlmSaving
} from '@/features/llmconfig/llmConfigSlice'
import type { LlmConfig } from '@/features/llmconfig/types'
import { useApiErrorMessage } from '@/i18n/useApiErrorMessage'
import { useAppDispatch, useAppSelector } from '@/store'

const HEAD_CLASS = 'text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground'

const inputClass = 'rounded-md border border-input bg-card px-2 py-1.5 text-sm text-foreground'

/** Job run outcome → pill tone; SKIPPED keeps its own neutral voice (INV-6). */
const RUN_TONE: Record<JobRunResult['status'], StatusTone> = {
  SUCCEEDED: 'done',
  FAILED: 'blocked',
  SKIPPED: 'neutral'
}

const CONTEXT_TONE: Record<ContextIndexRunView['status'], StatusTone> = {
  RUNNING: 'review',
  COMPLETED: 'done',
  FAILED: 'blocked'
}

/** The shared project picker (plan Decision 12 — this feature's own list). */
function ProjectPicker({
  projects,
  value,
  onSelectionChanged
}: {
  projects: Array<{ id: string; name: string }>
  value: string
  onSelectionChanged: (id: string) => void
}) {
  const { t } = useTranslation('runtime')
  return (
    <label className="flex flex-col gap-1 text-sm text-ink-soft">
      {t('picker.label')}
      <select
        value={value}
        onChange={(event) => onSelectionChanged(event.target.value)}
        className={`${inputClass} w-64`}
      >
        <option value="">{t('picker.placeholder')}</option>
        {projects.map((project) => (
          <option key={project.id} value={project.id}>
            {project.name}
          </option>
        ))}
      </select>
    </label>
  )
}

/** The editable form state — `newKey` ALWAYS starts empty (INV-2). */
const EMPTY_FORM: LlmKeyForm = {
  agentType: '',
  provider: '',
  model: '',
  maxTokens: 4096,
  temperature: 0.2,
  useOwnKey: false,
  newKey: ''
}

/**
 * The LLM panel (AC-5): one row per agent type, plus the create/edit form.
 * The key input is disabled when `useOwnKey` is false, never pre-filled from
 * a read, and never re-displays the masked value — the masked `apiKey`
 * renders ONLY as the "key on file" indicator beside the row.
 */
function LlmPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation(['runtime', 'common'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const configs = useAppSelector(selectLlmConfigs)
  const cache = useAppSelector(selectLlmCache)
  const error = useAppSelector(selectLlmError)
  const saving = useAppSelector(selectLlmSaving)
  const refusal = useAppSelector(selectLlmRefusal)

  const [form, setForm] = useState<LlmKeyForm>(EMPTY_FORM)
  const [formOpen, setFormOpen] = useState(false)

  useEffect(() => {
    if (projectId !== '') void dispatch(fetchLlmConfigs(projectId))
  }, [dispatch, projectId])

  const onFieldChanged = <K extends keyof LlmKeyForm>(field: K, value: LlmKeyForm[K]): void => {
    setForm((current) => ({ ...current, [field]: value }))
  }

  const onEditClicked = (row: LlmConfig): void => {
    setForm({
      agentType: row.agentType,
      provider: row.provider,
      model: row.model,
      maxTokens: row.maxTokens,
      temperature: row.temperature,
      useOwnKey: row.useOwnKey,
      // The key input starts EMPTY even on edit — the stored key is write-only
      // and the read is masked (INV-2). Re-entering is the explicit act.
      newKey: ''
    })
    setFormOpen(true)
  }

  const onSaveClicked = (): void => {
    void dispatch(saveLlmConfig({ projectId, form }))
      .unwrap()
      .then(() => {
        setFormOpen(false)
        setForm(EMPTY_FORM)
        void dispatch(fetchLlmConfigs(projectId))
      })
      .catch(() => {
        // A refusal or a mapped server error renders below; the typed key
        // stays in the form so the user can correct and retry.
      })
  }

  return (
    <section aria-label={t('llm.heading')} className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <SectionLabel>{t('llm.heading')}</SectionLabel>
        {!formOpen && (
          <button
            type="button"
            onClick={() => {
              setForm(EMPTY_FORM)
              setFormOpen(true)
            }}
            className="rounded-md border border-input px-2.5 py-1 text-xs text-ink-soft hover:bg-muted"
          >
            {t('llm.addRow')}
          </button>
        )}
      </div>

      <div className="px-4 py-3">
        {formOpen && (
          <form
            className="mb-4 grid gap-3 rounded-md border border-hairline bg-muted/40 px-3 py-3 sm:grid-cols-2 lg:grid-cols-3"
            onSubmit={(event) => {
              event.preventDefault()
              onSaveClicked()
            }}
          >
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('llm.form.agentType')}
              <input
                type="text"
                value={form.agentType}
                onChange={(event) => onFieldChanged('agentType', event.target.value)}
                className={`${inputClass} w-44 font-mono text-xs`}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('llm.form.provider')}
              <input
                type="text"
                value={form.provider}
                onChange={(event) => onFieldChanged('provider', event.target.value)}
                className={`${inputClass} w-44 font-mono text-xs`}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('llm.form.model')}
              <input
                type="text"
                value={form.model}
                onChange={(event) => onFieldChanged('model', event.target.value)}
                className={`${inputClass} w-44 font-mono text-xs`}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('llm.form.maxTokens')}
              <input
                type="number"
                min={1}
                value={form.maxTokens}
                onChange={(event) => onFieldChanged('maxTokens', Number(event.target.value))}
                className={`${inputClass} w-28`}
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('llm.form.temperature')}
              <input
                type="number"
                min={0}
                max={2}
                step={0.1}
                value={form.temperature}
                onChange={(event) => onFieldChanged('temperature', Number(event.target.value))}
                className={`${inputClass} w-28`}
                required
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-ink-soft">
              <input
                type="checkbox"
                checked={form.useOwnKey}
                onChange={(event) => onFieldChanged('useOwnKey', event.target.checked)}
                className="h-4 w-4"
              />
              {t('llm.form.useOwnKey')}
            </label>
            <label className="flex flex-col gap-1 text-sm text-ink-soft">
              {t('llm.form.key')}
              <input
                type="password"
                autoComplete="new-password"
                value={form.newKey}
                disabled={!form.useOwnKey}
                onChange={(event) => onFieldChanged('newKey', event.target.value)}
                className={`${inputClass} w-52 disabled:opacity-50`}
                aria-describedby="llm-key-hint"
              />
              <span id="llm-key-hint" className="text-xs text-muted-foreground">
                {t('llm.form.keyHint')}
              </span>
            </label>
            <div className="flex items-end gap-2">
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-60"
              >
                {saving ? t('common:states.loading') : t('common:actionsMore.save')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormOpen(false)
                  setForm(EMPTY_FORM)
                  dispatch(llmErrorDismissed())
                }}
                className="rounded-md border border-input px-3 py-1.5 text-sm text-ink-soft hover:bg-muted"
              >
                {t('common:actionsMore.cancel')}
              </button>
            </div>
          </form>
        )}

        {refusal !== null && (
          <p role="alert" className="mb-3 text-sm text-blocked">
            {t(`llm.refusal.${refusal}`)}
          </p>
        )}
        {error !== null && (
          <p role="alert" className="mb-3 text-sm text-blocked">
            {toMessage(error)}
          </p>
        )}

        {cache.loading ? (
          <LoadingPanel rows={2} />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/projects/:projectId/llm-config" />
        ) : error !== null ? (
          <ErrorPanel
            error={error}
            onRetryClicked={() => {
              dispatch(llmRetryClicked())
              void dispatch(fetchLlmConfigs(projectId))
            }}
          />
        ) : configs.length === 0 ? (
          <EmptyPanel body={t('llm.empty')} />
        ) : (
          <Table>
            <TableCaption className="sr-only">{t('llm.caption')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className={HEAD_CLASS + ' pl-3'}>
                  {t('llm.columns.agentType')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('llm.columns.provider')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('llm.columns.model')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('llm.columns.limits')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('llm.columns.key')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS + ' pr-3 text-right'}>
                  {t('llm.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {configs.map((row) => (
                <TableRow key={row.agentType}>
                  <TableHead scope="row" className="h-auto py-2.5 pl-3 align-top font-normal">
                    <span className="font-mono text-xs text-foreground">{row.agentType}</span>
                  </TableHead>
                  <TableCell className="py-2.5 align-top font-mono text-xs text-ink-soft">
                    {row.provider}
                  </TableCell>
                  <TableCell className="py-2.5 align-top font-mono text-xs text-ink-soft">
                    {row.model}
                  </TableCell>
                  <TableCell className="py-2.5 align-top text-xs text-muted-foreground">
                    {row.maxTokens} · {row.temperature}
                  </TableCell>
                  <TableCell className="py-2.5 align-top">
                    {row.apiKey !== '' ? (
                      <span className="flex items-center gap-1.5" title={t('llm.keyOnFileTitle')}>
                        <Badge variant="outline" className="font-normal">
                          {t('llm.keyOnFile')}
                        </Badge>
                        <span className="font-mono text-xs text-muted-foreground">
                          {row.apiKey}
                        </span>
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">{t('llm.noKey')}</span>
                    )}
                  </TableCell>
                  <TableCell className="py-2.5 pr-3 text-right align-top">
                    <button
                      type="button"
                      onClick={() => onEditClicked(row)}
                      className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted"
                    >
                      {t('common:actionsMore.edit')}
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  )
}

/** One scheduler job row — the four recorded facts, nothing derived. */
function JobRow({
  job,
  running,
  projectId,
  onRunClicked
}: {
  job: SchedulerJobView
  running: boolean
  projectId: string
  onRunClicked: (jobKey: string) => void
}) {
  const { t } = useTranslation('runtime')
  return (
    <TableRow>
      <TableHead scope="row" className="h-auto py-2.5 pl-3 align-top font-normal">
        <span className="font-mono text-xs text-foreground">{job.key}</span>
      </TableHead>
      <TableCell className="py-2.5 align-top">
        <StatusPill tone={job.enabled ? 'done' : 'neutral'}>
          {job.enabled ? t('scheduler.enabled') : t('scheduler.disabled')}
        </StatusPill>
      </TableCell>
      <TableCell className="py-2.5 align-top">
        <StatusPill tone={job.scheduled ? 'done' : 'neutral'}>
          {job.scheduled ? t('scheduler.scheduled') : t('scheduler.notScheduled')}
        </StatusPill>
      </TableCell>
      <TableCell className="py-2.5 align-top">
        <StatusPill tone={job.runnable ? 'done' : 'review'}>
          {job.runnable ? t('scheduler.runnable') : t('scheduler.notRunnable')}
        </StatusPill>
      </TableCell>
      <TableCell className="py-2.5 pr-3 text-right align-top">
        <button
          type="button"
          disabled={running}
          title={projectId === '' ? t('scheduler.runOrgWide') : t('scheduler.runForProject')}
          onClick={() => onRunClicked(job.key)}
          className="rounded-md border border-input px-2 py-1 text-xs text-ink-soft hover:bg-muted disabled:opacity-60"
        >
          {running ? t('scheduler.running') : t('scheduler.runNow')}
        </button>
      </TableCell>
    </TableRow>
  )
}

/**
 * The scheduler panel (AC-7): the four facts per job + Run now. There is
 * deliberately NO enable/disable control (no route exists — INV-6), and the
 * run outcome renders with `skipped` as itself.
 */
function SchedulerPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation('runtime')
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const jobs = useAppSelector(selectSchedulerJobs)
  const cache = useAppSelector(selectSchedulerJobsCache)
  const error = useAppSelector(selectSchedulerJobsError)
  const mutationError = useAppSelector(selectAutomationMutationError)
  const jobResult = useAppSelector(selectJobResult)
  const runningJobKey = useAppSelector(selectRunningJobKey)

  useEffect(() => {
    void dispatch(fetchSchedulerJobs())
  }, [dispatch])

  const onRunClicked = (jobKey: string): void => {
    void dispatch(
      triggerJob({ jobKey, projectId: projectId === '' ? null : projectId })
    )
  }

  return (
    <section aria-label={t('scheduler.heading')} className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <SectionLabel>{t('scheduler.heading')}</SectionLabel>
      </div>
      <div className="px-4 py-3">
        {mutationError !== null && (
          <p role="alert" className="mb-3 text-sm text-blocked">
            {toMessage(mutationError)}
          </p>
        )}
        {jobResult !== null && (
          <div role="status" className="mb-3 flex items-start gap-2 rounded-md border border-hairline bg-muted/40 px-3 py-2">
            <StatusPill tone={RUN_TONE[jobResult.status]}>
              {t(`scheduler.outcome.${jobResult.status}`)}
            </StatusPill>
            <span className="text-sm text-ink-soft">{jobResult.detail}</span>
            <button
              type="button"
              onClick={() => dispatch(jobResultDismissed())}
              className="ml-auto text-xs text-muted-foreground hover:text-foreground"
            >
              {t('common:actionsMore.close')}
            </button>
          </div>
        )}

        {cache.loading ? (
          <LoadingPanel rows={2} />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/scheduler/jobs" />
        ) : error !== null ? (
          <ErrorPanel
            error={error}
            onRetryClicked={() => {
              dispatch(automationRetryClicked())
              void dispatch(fetchSchedulerJobs())
            }}
          />
        ) : jobs.length === 0 ? (
          <EmptyPanel body={t('scheduler.empty')} />
        ) : (
          <Table>
            <TableCaption className="sr-only">{t('scheduler.caption')}</TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col" className={HEAD_CLASS + ' pl-3'}>
                  {t('scheduler.columns.key')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('scheduler.columns.enabled')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('scheduler.columns.scheduled')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS}>
                  {t('scheduler.columns.runnable')}
                </TableHead>
                <TableHead scope="col" className={HEAD_CLASS + ' pr-3 text-right'}>
                  {t('scheduler.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <JobRow
                  key={job.key}
                  job={job}
                  running={runningJobKey === job.key}
                  projectId={projectId}
                  onRunClicked={onRunClicked}
                />
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </section>
  )
}

/** The context-index panel (AC-7): the last run's report + manual trigger. */
function ContextIndexPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation(['runtime', 'common'])
  const toMessage = useApiErrorMessage()
  const dispatch = useAppDispatch()

  const run = useAppSelector(selectContextIndexRun)
  const cache = useAppSelector(selectContextIndexCache)
  const error = useAppSelector(selectContextIndexError)
  const running = useAppSelector(selectRunningIndex)
  const mutationError = useAppSelector(selectAutomationMutationError)

  useEffect(() => {
    if (projectId !== '') void dispatch(fetchContextIndexStatus(projectId))
  }, [dispatch, projectId])

  const onRunClicked = (): void => {
    void dispatch(triggerContextIndexRun(projectId)).then(() => {
      // The trigger answers the RUNNING report; pull the recorded status so
      // the completed/failed report replaces it.
      void dispatch(fetchContextIndexStatus(projectId))
    })
  }

  return (
    <section aria-label={t('context.heading')} className="rounded-lg border border-border bg-card">
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
        <SectionLabel>{t('context.heading')}</SectionLabel>
        {projectId !== '' && (
          <button
            type="button"
            onClick={onRunClicked}
            disabled={running}
            className="rounded-md border border-input px-2.5 py-1 text-xs text-ink-soft hover:bg-muted disabled:opacity-60"
          >
            {running ? t('context.running') : t('context.runNow')}
          </button>
        )}
      </div>
      <div className="px-4 py-3">
        {mutationError !== null && (
          <p role="alert" className="mb-3 text-sm text-blocked">
            {toMessage(mutationError)}
          </p>
        )}
        {projectId === '' ? (
          <EmptyPanel body={t('context.pickFirst')} />
        ) : cache.loading && run === null ? (
          <LoadingPanel rows={2} />
        ) : error !== null && error.status === 403 ? (
          <ForbiddenPanel requested="/{orgId}/projects/:projectId/context-index/status" />
        ) : error !== null ? (
          <ErrorPanel
            error={error}
            onRetryClicked={() => {
              dispatch(automationRetryClicked())
              void dispatch(fetchContextIndexStatus(projectId))
            }}
          />
        ) : run === null ? (
          <EmptyPanel body={t('context.empty')} />
        ) : (
          <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt>
                <SectionLabel>{t('context.fields.status')}</SectionLabel>
              </dt>
              <dd className="mt-1">
                <StatusPill tone={CONTEXT_TONE[run.status]}>
                  {t(`context.status.${run.status}`)}
                </StatusPill>
              </dd>
            </div>
            <div>
              <dt>
                <SectionLabel>{t('context.fields.triggeredBy')}</SectionLabel>
              </dt>
              <dd className="mt-1 text-sm text-foreground">{t(`context.trigger.${run.triggeredBy}`)}</dd>
            </div>
            <div>
              <dt>
                <SectionLabel>{t('context.fields.startedAt')}</SectionLabel>
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                {run.startedAt === null || run.startedAt === undefined
                  ? t('common:values.unknown')
                  : new Date(run.startedAt).toLocaleString()}
              </dd>
            </div>
            <div>
              <dt>
                <SectionLabel>{t('context.fields.duration')}</SectionLabel>
              </dt>
              <dd className="mt-1 text-sm text-foreground">
                {run.durationMs === null || run.durationMs === undefined
                  ? t('context.inFlight')
                  : t('context.durationValue', { ms: run.durationMs })}
              </dd>
            </div>
            <div>
              <dt>
                <SectionLabel>{t('context.fields.filesIndexed')}</SectionLabel>
              </dt>
              <dd className="v-figure mt-1 text-sm font-semibold tabular-nums text-foreground">
                {run.filesIndexed}
              </dd>
            </div>
            <div>
              <dt>
                <SectionLabel>{t('context.fields.chunks')}</SectionLabel>
              </dt>
              <dd className="v-figure mt-1 text-sm font-semibold tabular-nums text-foreground">
                {run.chunks}
              </dd>
            </div>
            <div>
              <dt>
                <SectionLabel>{t('context.fields.totalChunks')}</SectionLabel>
              </dt>
              <dd className="v-figure mt-1 text-sm font-semibold tabular-nums text-foreground">
                {run.totalChunks}
              </dd>
            </div>
            {run.error !== null && run.error !== undefined && (
              <div className="sm:col-span-2 lg:col-span-4">
                <dt>
                  <SectionLabel>{t('context.fields.error')}</SectionLabel>
                </dt>
                <dd className="mt-1 text-sm text-blocked">{run.error}</dd>
              </div>
            )}
          </dl>
        )}
      </div>
    </section>
  )
}

/**
 * `/admin/runtime` (PX-W6.5, AC-5 + AC-7) — per-project LLM configuration,
 * the scheduler jobs and the context-index state, driven by ONE shared
 * project picker (all three are project-scoped reads/writes).
 */
export default function RuntimePage() {
  const { t } = useTranslation('runtime')
  const dispatch = useAppDispatch()

  const projects = useAppSelector(selectAutomationProjects)
  const projectsCache = useAppSelector((s) => s.automation.projectsCache)

  const [projectId, setProjectId] = useState('')

  useEffect(() => {
    void dispatch(fetchAutomationProjects())
  }, [dispatch])

  return (
    <AppShell
      moduleLabel={t('chrome.moduleLabel')}
      breadcrumb={<span>{t('chrome.breadcrumb')}</span>}
      contextPill={t('chrome.contextPill')}
      nav={adminNav('admin')}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
        <h1 className="font-serif text-2xl font-semibold">{t('title')}</h1>
        <p className="text-sm text-muted-foreground">{t('lede')}</p>
      </header>

      <section aria-label={t('picker.label')} className="mt-4">
        {projectsCache.loading ? (
          <LoadingPanel rows={1} />
        ) : (
          <ProjectPicker projects={projects} value={projectId} onSelectionChanged={setProjectId} />
        )}
      </section>

      <div className="mt-4 flex flex-col gap-4">
        <LlmPanel projectId={projectId} />
        <SchedulerPanel projectId={projectId} />
        <ContextIndexPanel projectId={projectId} />
      </div>

      <p className="mt-6 border-t border-border pt-3 text-xs italic text-muted-foreground">
        {t('densityNote')}
      </p>
    </AppShell>
  )
}
